// helper functions to access stuff in the phone info object
// I am putting these at the top so ya don't have to scroll for them

/** gets the different phone makes we support in an array: eg. Polycom, GrandStream, etc */
export function getPhoneMakes(): Array<PhoneMake> {
  return Object.keys(phoneInfo).sort() as Array<PhoneMake>;
}

/** interface with model's key name and display name */
export interface DisplayPhoneModel {
  key: string;
  name: string;
}
/** gets all phone models of a given make in an array: eg. Polycom Soundpoint 301, Polycom VVX 311 */
export function getPhoneModels(make: PhoneMake): Array<DisplayPhoneModel> {
  const keys = Object.keys(phoneInfo[make].Models);
  const name = keys.map(key => phoneInfo[make].Models[key].Name);
  return keys.map((key, i) => ({
    key,
    name: name[i]
  }));
}

export function getModelFeatures(make: PhoneMake, model: string): PhoneFeatures {
  const makeFeatures = phoneInfo[make];
  const phoneFeatures = phoneInfo[make].Models[model];
  const features = {
    Directory: makeFeatures.Directory || false,
    NATKeepalive: makeFeatures.NATKeepalive || false,
    WebInterface: makeFeatures.WebInterface || false,
    WebPassword: makeFeatures.WebPassword || false,
    DigitMap: makeFeatures.DigitMap || false,
    Concurrent: makeFeatures.Concurrent || false
  };
  return { ...features, ...phoneFeatures };
}

export enum PhoneMake {
  "Polycom" = "Polycom",
  "Panasonic" = "Panasonic",
  "Cisco" = "Cisco",
  "Yealink" = "Yealink",
  "Grandstream" = "Grandstream"
}

export interface PhoneModel {
  Name: string;
  Lines: number;
  EasyTransfer?: boolean;
  BLF?: number;
  BLFOverlap?: boolean;
  Intercom?: boolean;
  EndToEndEncryption?: boolean;
  WebInterface?: boolean;
  WebPassword?: boolean;
  // required if webPassword is true
  PasswordLength?: number;
  SideCar?: Array<SideCarItem>;
}

export interface SideCarItem {
  Type: string;
  BLF: number;
  Multiple: number;
}

export interface PhoneFeatures extends PhoneModel {
  Directory: boolean;
  NATKeepalive: boolean;
  WebInterface: boolean;
  WebPassword: boolean;
  DigitMap: boolean;
  Concurrent: boolean;
}

export type PhoneInfoType = {
  [key in PhoneMake]: {
    Models: Record<string, PhoneModel>;
    Directory: boolean;
    NATKeepalive: boolean;
    WebInterface: boolean;
    WebPassword: boolean;
    DigitMap: boolean;
    Concurrent: boolean;
  };
};

export const phoneInfo: PhoneInfoType = {
  Polycom: {
    Models: {
      "Polycom Soundpoint 301": { Name: "Soundpoint 301", Lines: 2, EasyTransfer: true },
      "Polycom Soundpoint 320": { Name: "Soundpoint 320", Lines: 2, EasyTransfer: true },
      "Polycom Soundpoint 321": { Name: "Soundpoint 321", Lines: 2, EasyTransfer: true },
      "Polycom Soundpoint 330": { Name: "Soundpoint 330", Lines: 2, EasyTransfer: true },
      "Polycom Soundpoint 331": { Name: "Soundpoint 331", Lines: 2, EasyTransfer: true },
      "Polycom Soundpoint 335": { Name: "Soundpoint 335", Lines: 2, EasyTransfer: true },
      "Polycom Soundpoint 430": { Name: "Soundpoint 430", Lines: 3, EasyTransfer: true },
      "Polycom Soundpoint 450": {
        Name: "Soundpoint 450",
        Lines: 3,
        EasyTransfer: true,
        BLF: 2,
        BLFOverlap: true
      },
      "Polycom Soundpoint 501": { Name: "Soundpoint 501", Lines: 4, EasyTransfer: true },
      "Polycom Soundpoint 550": {
        Name: "Soundpoint 550",
        Lines: 4,
        EasyTransfer: true,
        BLF: 3,
        BLFOverlap: true
      },
      "Polycom Soundpoint 560": {
        Name: "Soundpoint 560",
        Lines: 4,
        EasyTransfer: true,
        BLF: 3,
        BLFOverlap: true
      },
      "Polycom Soundpoint 601": { Name: "Soundpoint 601", Lines: 6, EasyTransfer: true },
      "Polycom Soundpoint 650": {
        Name: "Soundpoint 650",
        Lines: 6,
        EasyTransfer: true,
        BLF: 5,
        BLFOverlap: true,
        SideCar: [{ Type: "Soundpoint IP Backlit", BLF: 14, Multiple: 3 }]
      },
      "Polycom Soundpoint 670": {
        Name: "Soundpoint 670",
        Lines: 6,
        EasyTransfer: true,
        BLF: 5,
        BLFOverlap: true,
        SideCar: [{ Type: "Soundpoint IP Color", BLF: 14, Multiple: 3 }]
      },
      "Polycom VVX 101": {
        Name: "Polycom VVX 101",
        Lines: 1,
        EasyTransfer: true,
        Intercom: true,
        EndToEndEncryption: true
      },
      "Polycom VVX 150": {
        Name: "Polycom VVX 150",
        Lines: 2,
        EasyTransfer: true,
        Intercom: true,
        EndToEndEncryption: true
      },
      "Polycom VVX 201": {
        Name: "Polycom VVX 201",
        Lines: 2,
        EasyTransfer: true,
        Intercom: true,
        EndToEndEncryption: true
      },
      "Polycom VVX 250": {
        Name: "Polycom VVX 250",
        Lines: 4,
        EasyTransfer: true,
        Intercom: true,
        EndToEndEncryption: true,
        BLF: 3,
        BLFOverlap: true
      },
      "Polycom VVX 300": {
        Name: "Polycom VVX 300",
        Lines: 6,
        EasyTransfer: true,
        Intercom: true,
        EndToEndEncryption: true,
        BLF: 5,
        BLFOverlap: true,
        SideCar: [{ Type: "VVX Expansion Module", BLF: 44, Multiple: 1 }]
      },
      "Polycom VVX 301": {
        Name: "Polycom VVX 301",
        Lines: 6,
        EasyTransfer: true,
        Intercom: true,
        EndToEndEncryption: true,
        BLF: 5,
        BLFOverlap: true,
        SideCar: [{ Type: "VVX Expansion Module", BLF: 44, Multiple: 1 }]
      },
      "Polycom VVX 310": {
        Name: "Polycom VVX 310",
        Lines: 6,
        EasyTransfer: true,
        Intercom: true,
        EndToEndEncryption: true,
        BLF: 5,
        BLFOverlap: true,
        SideCar: [{ Type: "VVX Expansion Module", BLF: 44, Multiple: 1 }]
      },
      "Polycom VVX 311": {
        Name: "Polycom VVX 311",
        Lines: 6,
        EasyTransfer: true,
        Intercom: true,
        EndToEndEncryption: true,
        BLF: 5,
        BLFOverlap: true,
        SideCar: [{ Type: "VVX Expansion Module", BLF: 44, Multiple: 1 }]
      },
      "Polycom VVX 350": {
        Name: "Polycom VVX 350",
        Lines: 6,
        EasyTransfer: true,
        Intercom: true,
        EndToEndEncryption: true,
        BLF: 5,
        BLFOverlap: true
      },
      "Polycom VVX 400": {
        Name: "Polycom VVX 400",
        Lines: 12,
        EasyTransfer: true,
        Intercom: true,
        EndToEndEncryption: true,
        BLF: 11,
        BLFOverlap: true,
        SideCar: [{ Type: "VVX Expansion Module", BLF: 38, Multiple: 1 }]
      },
      "Polycom VVX 401": {
        Name: "Polycom VVX 401",
        Lines: 12,
        EasyTransfer: true,
        Intercom: true,
        EndToEndEncryption: true,
        BLF: 11,
        BLFOverlap: true,
        SideCar: [{ Type: "VVX Expansion Module", BLF: 38, Multiple: 1 }]
      },
      "Polycom VVX 410": {
        Name: "Polycom VVX 410",
        Lines: 12,
        EasyTransfer: true,
        Intercom: true,
        EndToEndEncryption: true,
        BLF: 11,
        BLFOverlap: true,
        SideCar: [{ Type: "VVX Expansion Module", BLF: 38, Multiple: 1 }]
      },
      "Polycom VVX 411": {
        Name: "Polycom VVX 411",
        Lines: 12,
        EasyTransfer: true,
        Intercom: true,
        EndToEndEncryption: true,
        BLF: 11,
        BLFOverlap: true,
        SideCar: [{ Type: "VVX Expansion Module", BLF: 38, Multiple: 1 }]
      },
      "Polycom VVX 450": {
        Name: "Polycom VVX 450",
        Lines: 12,
        EasyTransfer: true,
        Intercom: true,
        EndToEndEncryption: true,
        BLF: 11,
        BLFOverlap: true,
        SideCar: [{ Type: "VVX EM50 Expansion Module", BLF: 38, Multiple: 1 }]
      },
      "Polycom VVX 500": {
        Name: "Polycom VVX 500",
        Lines: 12,
        EasyTransfer: true,
        Intercom: true,
        EndToEndEncryption: true,
        BLF: 11,
        BLFOverlap: true,
        SideCar: [{ Type: "VVX Expansion Module", BLF: 38, Multiple: 1 }]
      },
      "Polycom VVX 501": {
        Name: "Polycom VVX 501",
        Lines: 12,
        EasyTransfer: true,
        Intercom: true,
        EndToEndEncryption: true,
        BLF: 11,
        BLFOverlap: true,
        SideCar: [{ Type: "VVX Expansion Module", BLF: 38, Multiple: 1 }]
      },
      "Polycom VVX 600": {
        Name: "Polycom VVX 600",
        Lines: 16,
        EasyTransfer: true,
        Intercom: true,
        EndToEndEncryption: true,
        BLF: 15,
        BLFOverlap: true,
        SideCar: [{ Type: "VVX Expansion Module", BLF: 34, Multiple: 1 }]
      },
      "Polycom VVX 601": {
        Name: "Polycom VVX 601",
        Lines: 16,
        EasyTransfer: true,
        Intercom: true,
        EndToEndEncryption: true,
        BLF: 15,
        BLFOverlap: true,
        SideCar: [{ Type: "VVX Expansion Module", BLF: 34, Multiple: 1 }]
      },
      "Polycom VVX 1500": { Name: "Polycom VVX 1500", Lines: 6, EasyTransfer: true },
      "Polycom Soundstation 4000": { Name: "Polycom Soundstation 4000", Lines: 1 },
      "Polycom Soundstation 5000": { Name: "Polycom Soundstation 5000", Lines: 1 },
      "Polycom Soundstation 6000": { Name: "Polycom Soundstation 6000", Lines: 1 },
      "Polycom Soundstation 7000": { Name: "Polycom Soundstation 7000", Lines: 1 },
      "Polycom Trio 8300": { Name: "Polycom Trio 8300", Lines: 1 },
      "Polycom Trio 8500": { Name: "Polycom Trio 8500", Lines: 1 },
      "Polycom Trio 8800": { Name: "Polycom Trio 8800", Lines: 1 },
      "Polycom CCX 400": {
        Name: "CCX 400",
        Lines: 24,
        BLF: 50,
        BLFOverlap: true,
        EasyTransfer: true,
        Intercom: true,
        EndToEndEncryption: true
      },
      "Polycom CCX 500": {
        Name: "CCX 500",
        Lines: 24,
        BLF: 50,
        BLFOverlap: true,
        EasyTransfer: true,
        Intercom: true,
        EndToEndEncryption: true
      },
      "Polycom CCX 600": {
        Name: "CCX 600",
        Lines: 24,
        BLF: 50,
        BLFOverlap: true,
        EasyTransfer: true,
        Intercom: true,
        EndToEndEncryption: true
      },
      "Polycom CCX 700": {
        Name: "CCX 700",
        Lines: 24,
        BLF: 50,
        BLFOverlap: true,
        EasyTransfer: true,
        Intercom: true,
        EndToEndEncryption: true
      },
      "Polycom Edge E 100": {
        Name: "Edge E 100",
        Lines: 8,
        BLF: 7,
        BLFOverlap: true,
        WebPassword: true,
        PasswordLength: 4,
        EasyTransfer: true,
        Intercom: true,
        EndToEndEncryption: true
      },
      "Polycom Edge E 220": {
        Name: "Edge E 220",
        Lines: 8,
        BLF: 7,
        BLFOverlap: true,
        WebPassword: true,
        PasswordLength: 4,
        EasyTransfer: true,
        Intercom: true,
        EndToEndEncryption: true
      },
      "Polycom Edge E 300": {
        Name: "Edge E 300 / 320 / 350",
        Lines: 32,
        BLF: 31,
        BLFOverlap: true,
        WebPassword: true,
        PasswordLength: 4,
        EasyTransfer: true,
        Intercom: true,
        EndToEndEncryption: true
      },
      "Polycom Edge E 400": {
        Name: "Edge E 400 / 450",
        Lines: 24,
        BLF: 43,
        BLFOverlap: true,
        WebPassword: true,
        PasswordLength: 4,
        EasyTransfer: true,
        Intercom: true,
        EndToEndEncryption: true,
        SideCar: [{ Type: "Edge E EM", BLF: 43, Multiple: 1 }]
        // real sidecar schema is { 'Type': 'Edge E EM', 'BLF': 66, 'Multiple': 1 } but we need a different calculation for expansion module to work with our UI
      },
      "Polycom Edge E 500": {
        Name: "Edge E 500 / 550",
        Lines: 34,
        BLF: 47,
        BLFOverlap: true,
        WebPassword: true,
        PasswordLength: 4,
        EasyTransfer: true,
        Intercom: true,
        EndToEndEncryption: true,
        SideCar: [{ Type: "Edge E EM", BLF: 96, Multiple: 1 }]
        // real sidecar schema is { 'Type': 'Edge E EM', 'BLF': 65, 'Multiple': 2 } but we need a different calculation for expansion module to work with our UI
      }
    },
    Directory: true,
    NATKeepalive: true,
    WebInterface: true,
    WebPassword: true,
    DigitMap: true,
    Concurrent: true
  },
  Panasonic: {
    Models: {
      "Panasonic KX-HDV130": { Name: "KX-HDV130", Lines: 2 },
      "Panasonic KX-HDV230": {
        Name: "KX-HDV230",
        Lines: 6,
        BLF: 24,
        BLFOverlap: false,
        SideCar: [{ Type: "KX-HDV20", BLF: 40, Multiple: 2 }]
      },
      "Panasonic KX-TGP600": { Name: "KX-TGP600", Lines: 8 }
    },
    Directory: false,
    NATKeepalive: false,
    WebInterface: true,
    WebPassword: false,
    DigitMap: true,
    Concurrent: false
  },
  Cisco: {
    Models: {
      "Cisco 7811": { Name: "7811", Lines: 1 },
      "Cisco 7821": { Name: "7821", Lines: 2, BLF: 1, BLFOverlap: true },
      "Cisco 7841": { Name: "7841", Lines: 4, BLF: 3, BLFOverlap: true },
      "Cisco 7861": { Name: "7861", Lines: 16, BLF: 15, BLFOverlap: true },
      "Cisco 8811": { Name: "8811", Lines: 5, BLF: 9, BLFOverlap: true },
      "Cisco 8831": { Name: "8831", Lines: 1 },
      "Cisco 8841": { Name: "8841", Lines: 5, BLF: 9, BLFOverlap: true },
      "Cisco 8845": { Name: "8845", Lines: 5, BLF: 9, BLFOverlap: true },
      "Cisco 8851": { Name: "8851", Lines: 5, BLF: 9, BLFOverlap: true },
      "Cisco 8861": { Name: "8861", Lines: 5, BLF: 9, BLFOverlap: true },
      "Cisco 8865": { Name: "8865", Lines: 5, BLF: 9, BLFOverlap: true },
      "Cisco ATA 191": { Name: "ATA 191", Lines: 2 },
      "Cisco ATA 192": { Name: "ATA 192", Lines: 2 },
      "Cisco Linksys SPA901": { Name: "Linksys SPA901", Lines: 1 },
      "Cisco Linksys SPA921": { Name: "Linksys SPA921", Lines: 1 },
      "Cisco Linksys SPA922": { Name: "Linksys SPA922", Lines: 1 },
      "Cisco Linksys SPA941": { Name: "Linksys SPA941", Lines: 4, BLF: 3, BLFOverlap: true },
      "Cisco Linksys SPA942": { Name: "Linksys SPA942", Lines: 4, BLF: 3, BLFOverlap: true },
      "Cisco Linksys SPA962": { Name: "Linksys SPA962", Lines: 6, BLF: 5, BLFOverlap: true },
      "Cisco Linksys SPA2102": { Name: "Linksys SPA2102", Lines: 2 },
      "Cisco Linksys SPA3102": { Name: "Linksys SPA3102", Lines: 2 },
      "Cisco Linksys SPA8000": { Name: "Linksys SPA8000", Lines: 8 },
      "Cisco Linksys SPA8800": { Name: "Linksys SPA8800", Lines: 4 },
      "Cisco SPA112": { Name: "SPA112", Lines: 2 },
      "Cisco SPA303": { Name: "SPA303", Lines: 3 },
      "Cisco SPA501G": {
        Name: "SPA501G",
        Lines: 8,
        EasyTransfer: true,
        BLF: 7,
        BLFOverlap: true,
        SideCar: [{ Type: "SPA500S", BLF: 32, Multiple: 1 }]
      },
      "Cisco SPA502G": {
        Name: "SPA502G",
        Lines: 1,
        EasyTransfer: true,
        BLF: 0,
        BLFOverlap: true,
        SideCar: [{ Type: "SPA500S", BLF: 32, Multiple: 1 }]
      },
      "Cisco SPA504G": {
        Name: "SPA504G",
        Lines: 4,
        EasyTransfer: true,
        BLF: 3,
        BLFOverlap: true,
        SideCar: [{ Type: "SPA500S", BLF: 32, Multiple: 1 }]
      },
      "Cisco SPA508G": {
        Name: "SPA508G",
        Lines: 8,
        EasyTransfer: true,
        BLF: 7,
        BLFOverlap: true,
        SideCar: [{ Type: "SPA500S", BLF: 32, Multiple: 1 }]
      },
      "Cisco SPA509G": {
        Name: "SPA509G",
        Lines: 12,
        EasyTransfer: true,
        BLF: 11,
        BLFOverlap: true,
        SideCar: [{ Type: "SPA500S", BLF: 32, Multiple: 1 }]
      },
      "Cisco SPA512G": {
        Name: "SPA512G",
        Lines: 1,
        EasyTransfer: true,
        BLF: 0,
        BLFOverlap: true,
        SideCar: [{ Type: "SPA500S", BLF: 32, Multiple: 1 }]
      },
      "Cisco SPA514G": {
        Name: "SPA514G",
        Lines: 4,
        EasyTransfer: true,
        BLF: 3,
        BLFOverlap: true,
        SideCar: [{ Type: "SPA500S", BLF: 32, Multiple: 1 }]
      },
      "Cisco SPA525G": {
        Name: "SPA525G",
        Lines: 5,
        EasyTransfer: true,
        BLF: 4,
        BLFOverlap: true,
        SideCar: [{ Type: "SPA500S", BLF: 32, Multiple: 1 }]
      },
      "Cisco SPA525G2": {
        Name: "SPA525G2",
        Lines: 5,
        EasyTransfer: true,
        BLF: 4,
        BLFOverlap: true,
        SideCar: [{ Type: "SPA500S", BLF: 32, Multiple: 1 }]
      }
    },
    Directory: false,
    NATKeepalive: false,
    WebInterface: true,
    WebPassword: false,
    DigitMap: false,
    Concurrent: false
  },
  Yealink: {
    Models: {
      "Yealink CP860": { Name: "CP860", Lines: 1 },
      "Yealink CP920": { Name: "CP920", Lines: 1 },
      "Yealink CP960": { Name: "CP960", Lines: 1, BLF: 29, BLFOverlap: false },
      "Yealink SIP-T19P": { Name: "SIP-T19P / T19E2", Lines: 1 },
      "Yealink SIP-T20P": { Name: "SIP-T20P", Lines: 2, BLF: 1, BLFOverlap: true },
      "Yealink SIP-T21P": { Name: "SIP-T21P / T21E2", Lines: 2, BLF: 1, BLFOverlap: true },
      "Yealink SIP-T22P": { Name: "SIP-T22P", Lines: 3, BLF: 2, BLFOverlap: true },
      "Yealink SIP-T23P": { Name: "SIP-T23P", Lines: 3, BLF: 2, BLFOverlap: true },
      "Yealink SIP-T26P": { Name: "SIP-T26P", Lines: 3, BLF: 12, BLFOverlap: true },
      "Yealink SIP-T27P": { Name: "SIP-T27P / T27G", Lines: 6, BLF: 8, BLFOverlap: true },
      "Yealink SIP-T28P": { Name: "SIP-T28P", Lines: 6, BLF: 15, BLFOverlap: true },
      "Yealink SIP-T29G": { Name: "SIP-T29G", Lines: 16, BLF: 27, BLFOverlap: true },
      "Yealink SIP-T30G": { Name: "SIP-T30G / T30P", Lines: 1 },
      "Yealink SIP-T31G": { Name: "SIP-T31G / T31P", Lines: 2, BLF: 1, BLFOverlap: true },
      "Yealink SIP-T32G": { Name: "SIP-T32G", Lines: 3, BLF: 2, BLFOverlap: true },
      "Yealink SIP-T33G": { Name: "SIP-T33G / T33P", Lines: 4, BLF: 3, BLFOverlap: true },
      "Yealink SIP-T38G": { Name: "SIP-T38G", Lines: 6, BLF: 15, BLFOverlap: true },
      "Yealink SIP-T40G": { Name: "SIP-T40S / T40G", Lines: 3, BLF: 2, BLFOverlap: true },
      "Yealink SIP-T41P": { Name: "SIP-T41S / T41P", Lines: 6, BLF: 14, BLFOverlap: true },
      "Yealink SIP-T42G": { Name: "SIP-T42S / T42G / T42U", Lines: 12, BLF: 14, BLFOverlap: true },
      "Yealink SIP-T43U": { Name: "SIP-T43U", Lines: 12, BLF: 11, BLFOverlap: true },
      "Yealink SIP-T46G": {
        Name: "SIP-T46G / T46S / T46U",
        Lines: 16,
        BLF: 26,
        BLFOverlap: true,
        SideCar: [{ Type: "EXT40", BLF: 40, Multiple: 2 }]
      },
      "Yealink SIP-T48G": {
        Name: "SIP-T48G / T48S / T48U / T48 Pro",
        Lines: 16,
        BLF: 28,
        BLFOverlap: true,
        SideCar: [{ Type: "EXT40", BLF: 40, Multiple: 2 }]
      },
      "Yealink SIP-T49G": { Name: "SIP VP-T49G", Lines: 16, BLF: 28, BLFOverlap: true },
      "Yealink SIP-T52": { Name: "SIP-T52S", Lines: 12, BLF: 20, BLFOverlap: true },
      "Yealink SIP-T54": { Name: "SIP-T54S", Lines: 16, BLF: 25, BLFOverlap: true },
      "Yealink SIP-T56": { Name: "SIP-T56A", Lines: 16, BLF: 25, BLFOverlap: true },
      "Yealink SIP-T58": {
        Name: "SIP-T58V / T58A",
        Lines: 16,
        BLF: 26,
        BLFOverlap: true,
        SideCar: [{ Type: "EXP50", BLF: 40, Multiple: 4 }]
      },
      "Yealink SIP-T53W": {
        Name: "SIP-T53W",
        Lines: 12,
        BLF: 20,
        BLFOverlap: true,
        SideCar: [{ Type: "EXP50", BLF: 40, Multiple: 4 }]
      },
      "Yealink SIP-T54W": {
        Name: "SIP-T54W",
        Lines: 16,
        BLF: 26,
        BLFOverlap: true,
        SideCar: [{ Type: "EXP50", BLF: 40, Multiple: 4 }]
      },
      "Yealink SIP-T57W": {
        Name: "SIP-T57W",
        Lines: 16,
        BLF: 28,
        BLFOverlap: true,
        SideCar: [{ Type: "EXP50", BLF: 40, Multiple: 4 }]
      },
      "Yealink VP-2009P": { Name: "VP-2009P", Lines: 2, WebInterface: false },
      "Yealink VP530": { Name: "VP530", Lines: 4, BLF: 18, BLFOverlap: true },
      "Yealink W52P": { Name: "W52P", Lines: 5 },
      "Yealink W56P": { Name: "W56P", Lines: 5 },
      "Yealink W60B": { Name: "W60B", Lines: 8 },
      "Yealink W60P": { Name: "W60P", Lines: 8 },
      "Yealink W70": { Name: "W70", Lines: 10 }
    },
    Directory: false,
    NATKeepalive: false,
    WebInterface: true,
    WebPassword: true,
    DigitMap: false,
    Concurrent: false
  },
  Grandstream: {
    Models: {
      "Grandstream DP750": { Name: "DP750", Lines: 10, WebInterface: false },
      "Grandstream GAC2500": {
        Name: "GAC2500",
        Lines: 6,
        WebInterface: false,
        BLF: 30,
        BLFOverlap: false
      },
      "Grandstream GRP2612": {
        Name: "GRP2612 / GRP2612P",
        Lines: 3,
        BLF: 15,
        BLFOverlap: true
      },
      "Grandstream GRP2613": {
        Name: "GRP2613",
        Lines: 4,
        BLF: 23,
        BLFOverlap: true
      },
      "Grandstream GRP2614": {
        Name: "GRP2614",
        Lines: 4,
        BLF: 39,
        BLFOverlap: true
      },
      "Grandstream GRP2615": {
        Name: "GRP2615",
        Lines: 5,
        BLF: 39,
        BLFOverlap: true
      },
      "Grandstream GRP2616": {
        Name: "GRP2616",
        Lines: 6,
        BLF: 47,
        BLFOverlap: true
      },
      "Grandstream GRP2624": {
        Name: "GRP2624",
        Lines: 4,
        BLF: 11,
        BLFOverlap: true
      },
      "Grandstream GRP2634": {
        Name: "GRP2634",
        Lines: 4,
        BLF: 39,
        BLFOverlap: true
      },
      "Grandstream GRP2670": {
        Name: "GRP2670",
        Lines: 4,
        BLF: 47,
        BLFOverlap: true
      },
      "Grandstream GXP1160": { Name: "GXP1160", Lines: 1, WebInterface: false },
      "Grandstream GXP1165": { Name: "GXP1165", Lines: 1, WebInterface: false },
      "Grandstream GXP1405": { Name: "GXP1405", Lines: 2, WebInterface: false },
      "Grandstream GXP1450": { Name: "GXP1450", Lines: 2, WebInterface: false },
      "Grandstream GXP1610": { Name: "GXP1610", Lines: 1 },
      "Grandstream GXP1620": { Name: "GXP1620", Lines: 2 },
      "Grandstream GXP1625": { Name: "GXP1625", Lines: 2 },
      "Grandstream GXP1628": { Name: "GXP1628", Lines: 2, BLF: 9, BLFOverlap: true },
      "Grandstream GXP1630": { Name: "GXP1630", Lines: 3, BLF: 10, BLFOverlap: true },
      "Grandstream GXP1760": { Name: "GXP1760 / GXP1760W", Lines: 3, BLF: 24, BLFOverlap: true },
      "Grandstream GXP1780": { Name: "GXP1780", Lines: 4, BLF: 24, BLFOverlap: true },
      "Grandstream GXP1782": { Name: "GXP1782", Lines: 4, BLF: 24, BLFOverlap: true },
      "Grandstream GXP2124": {
        Name: "GXP2124",
        Lines: 4,
        WebInterface: false,
        BLF: 31,
        BLFOverlap: true
      },
      "Grandstream GXP2100": {
        Name: "GXP2100",
        Lines: 4,
        WebInterface: false,
        BLF: 10,
        BLFOverlap: true
      },
      "Grandstream GXP2110": {
        Name: "GXP2110",
        Lines: 4,
        WebInterface: false,
        BLF: 21,
        BLFOverlap: true
      },
      "Grandstream GXP2120": {
        Name: "GXP2120",
        Lines: 6,
        WebInterface: false,
        BLF: 17,
        BLFOverlap: true
      },
      "Grandstream GXP2130": { Name: "GXP2130", Lines: 3, BLF: 10, BLFOverlap: true },
      "Grandstream GXP2135": { Name: "GXP2135", Lines: 4, BLF: 30, BLFOverlap: true },
      "Grandstream GXP2140": {
        Name: "GXP2140",
        Lines: 4,
        BLF: 29,
        BLFOverlap: true,
        SideCar: [{ Type: "GXP2200EXT", BLF: 40, Multiple: 1 }]
      },
      "Grandstream GXP2160": { Name: "GXP2160", Lines: 6, BLF: 29, BLFOverlap: true },
      "Grandstream GXP2170": {
        Name: "GXP2170",
        Lines: 6,
        BLF: 29,
        BLFOverlap: true,
        SideCar: [{ Type: "GXP2200EXT", BLF: 40, Multiple: 1 }]
      },
      "Grandstream GXP2200": { Name: "GXP2200", Lines: 6, WebInterface: false },
      "Grandstream GXV3140": { Name: "GXV3140", Lines: 3, WebInterface: false },
      "Grandstream GXV3175": { Name: "GXV3175", Lines: 3, WebInterface: false },
      "Grandstream GXV3240": {
        Name: "GXV3240",
        Lines: 6,
        WebInterface: false,
        BLF: 30,
        BLFOverlap: false
      },
      "Grandstream GXV3275": {
        Name: "GXV3275",
        Lines: 6,
        WebInterface: false,
        BLF: 50,
        BLFOverlap: false
      },
      "Grandstream GXV3350": {
        Name: "GXV3350",
        Lines: 16,
        WebInterface: false,
        BLF: 50,
        BLFOverlap: false
      },
      "Grandstream GXV3370": {
        Name: "GXV3370",
        Lines: 16,
        WebInterface: false,
        BLF: 50,
        BLFOverlap: false
      },
      "Grandstream GXV3380": {
        Name: "GXV3380",
        Lines: 16,
        WebInterface: false,
        BLF: 50,
        BLFOverlap: false
      },
      "Grandstream GXW4216": { Name: "GXW4216", Lines: 16, WebInterface: false },
      "Grandstream GXW4224": { Name: "GXW4224", Lines: 24, WebInterface: false },
      "Grandstream GXW4232": { Name: "GXW4232", Lines: 32, WebInterface: false },
      "Grandstream HT503": { Name: "HT503", Lines: 2, WebInterface: false },
      "Grandstream HT502": { Name: "HT502", Lines: 2, WebInterface: false },
      "Grandstream HT701": { Name: "HT701", Lines: 1, WebInterface: false },
      "Grandstream HT702": { Name: "HT702", Lines: 2, WebInterface: false },
      "Grandstream HT704": { Name: "HT704", Lines: 4, WebInterface: false },
      "Grandstream HT801": { Name: "HT801", Lines: 1, WebInterface: false },
      "Grandstream HT802": { Name: "HT802", Lines: 2, WebInterface: false },
      "Grandstream HT812": { Name: "HT812", Lines: 2, WebInterface: false },
      "Grandstream HT813": { Name: "HT813", Lines: 2, WebInterface: false },
      "Grandstream HT814": { Name: "HT814", Lines: 4, WebInterface: false },
      "Grandstream HT818": {
        Name: "HT818",
        Lines: 8,
        WebPassword: false,
        WebInterface: false
      },
      "Grandstream WP810": { Name: "WP810", Lines: 2, WebInterface: false },
      "Grandstream WP820": {
        Name: "WP820",
        Lines: 2,
        WebInterface: false,
        PasswordLength: 6
      },
      "Grandstream WP822": {
        Name: "WP822",
        Lines: 2,
        WebInterface: false,
        PasswordLength: 6
      },
      "Grandstream WP825": {
        Name: "WP825",
        Lines: 2,
        WebInterface: false,
        PasswordLength: 6
      }
    },
    Directory: false,
    NATKeepalive: false,
    WebInterface: true,
    WebPassword: true,
    DigitMap: true,
    Concurrent: false
  }
};
